<template>
	<div>
		<loader v-if="!activeItem"/>
		<div v-else class="sections">
			<div class="section--full flex flex-wrap content-between items-start gap-2">
				<header>
					<div class="flex content-between gap-2 items-center">
						<h1 class="alpha heavy" v-html="$present('clientName', activeItem)"/>
						<div class="flex content-between gap-1 items-center">
							<dl v-if="activeItem.portalAvailable">
								<dt class="sr-only">Portal Status</dt>
								<dd>
									<status-badge
										v-if="!activeItem.portalRegistered" group="portal"
										status="registrationPending"/>
									<status-badge v-else group="portal" status="registered"/>
								</dd>
							</dl>
							<v-contextual>
								<ul>
									<template v-if="$can('sendPortalRegistrationEmail', activeItem)">
										<v-contextual-option
											@click.prevent="openPortalLinkModal()">Copy Registration Link</v-contextual-option>
										<v-contextual-option
											@click.prevent="sendPortalEmail()">Send Registration Email</v-contextual-option>
									</template>
									<template v-if="$can('sendPortalLoginEmail', activeItem)">
										<v-contextual-option
											@click.prevent="openPortalLinkModal()">Copy Login Link</v-contextual-option>
										<v-contextual-option
											@click.prevent="sendPortalEmail()">Send Login Email</v-contextual-option>
									</template>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('issueIdCheck', activeItem)"
										@click.prevent="openIssueIdCheckModal()">Issue ID Check</v-contextual-option>
									<template v-if="$can('sendIdEmail', activeItem)">
										<v-contextual-option
											@click.prevent="openIdLinkModal()">Copy ID Link</v-contextual-option>
										<v-contextual-option
											@click.prevent="sendIdEmail()">Send ID Email</v-contextual-option>
									</template>
								</ul>
							</v-contextual>
						</div>
					</div>
					<dl class="grid-list mt-1">
						<dt class="icon--id">ID</dt>
						<dd v-html="activeItem.id"/>
						<dt class="icon--contact--phone">Phone number</dt>
						<dd v-html="client.phone || '—'"/>
						<dt class="icon--contact--email">Email</dt>
						<dd v-html="client.email || '—'"/>
						<dt class="icon--contact--address">Correspondence address</dt>
						<dd v-if="!client.correspondenceAddress">—</dd>
						<Address v-else :address="client.correspondenceAddress"/>
						<template v-if="activeItem.portalAvailable">
							<dt :class="tasksClass">Tasks</dt>
							<dd>
								<button
									class="button button--text"
									@click.prevent="openTaskListModal()">{{ numberOfCompleteTasks }} / {{ numberOfTasks }}
								</button>
							</dd>
						</template>
						<template v-if="client.relatedClients.length">
							<dt class="icon--clients">Related clients</dt>
							<dd>
								<button
									class="button button--text"
									v-html="client.relatedClients.length" @click.prevent="openRelatedClientsModal()"/>
							</dd>
						</template>
					</dl>
				</header>
				<div class="sections section section--expanded">
					<section v-if="idCheck" class="section--full section--floating">
						<div class="flex gap-2 content-between items-center">
							<h2 class="beta heavy">ID Check</h2>
							<div class="flex gap-1 content-between items-center">
								<ul v-if="$can('seeResult', idCheck)" class="actions actions--icons">
									<li>
										<button
											class="button--icon icon--view"
											@click="openIdCheckModal()">View ID check
										</button>
									</li>
									<li>
										<button
											class="button--icon icon--pdf"
											@click.prevent="viewFile(idCheck.pdfUri)">Open ID check PDF
										</button>
									</li>
								</ul>
								<dl>
									<dt class="sr-only">Status</dt>
									<status-badge :status="idCheck.status" group="id-check"/>
								</dl>
							</div>
						</div>
						<template v-if="$can('seeResult', idCheck)">
							<!--							<dl class="id-check__check-list mt-1">-->
							<!--								<div-->
							<!--									:class="checkClass(idCheck.result)" class="id-check__check-list__check">-->
							<!--									<dt>Recommendation</dt>-->
							<!--									<dd v-html="stringer(idCheck.result.recommendation).ucWords().join(' ').value"/>-->
							<!--								</div>-->
							<!--							</dl>-->
							<dl class="mt-1">
								<div class="flex gap-2 items-center content-between">
									<dt class="heavy">Recommendation</dt>
									<dd>
										<status-badge
											:status="idCheck.result.recommendation" group="id-check-recommendation"/>
									</dd>
								</div>
								<!--								<div class="flex gap-2 items-center content-between mt-1">-->
								<!--									<dt class="heavy">Expires</dt>-->
								<!--									<dd v-html="$present('date', idCheck.expiresAt)"/>-->
								<!--								</div>-->
							</dl>
						</template>
					</section>
					<section v-if="activeItem.paid !== null" class="section--full section--floating">
						<h2 class="beta heavy">Payments</h2>
						<dl class="mt-1">
							<div class="flex gap-2 content-between">
								<dt class="heavy">Balance</dt>
								<dd class="beta heavy" v-html="$present('price', activeItem.balance)"/>
							</div>
							<div class="flex gap-2 content-between">
								<dt class="heavy">Paid</dt>
								<dd class="beta heavy" v-html="$present('price', activeItem.paidBalance)"/>
							</div>
						</dl>
					</section>
				</div>
			</div>
			<div class="sections section--full">
				<v-tabs ref="tabs" v-slot="{ goToTab }" class="tabs--switch tabs--switch--shadow section--full">
					<v-tab
						id="cases"
						class="section--full"
						label="Cases">
						<header class="mb-2">
							<h2 class="beta heavy">Cases</h2>
						</header>
						<div v-if="!client.instructions.length" class="section section--empty">
							<div class="editorial center">
								<p class="beta">There are no cases related to this client.</p>
							</div>
							<ul class="actions actions--center actions--small-margin">
								<li v-if="client.leads.length">
									<button
										class="button button--primary button--small"
										@click.prevent="goToTab('leads')">View leads
									</button>
								</li>
							</ul>
						</div>
						<div v-else class="sections">
							<Instruction
								v-for="(instruction, instructionIndex) in client.instructions"
								:key="instructionIndex"
								:client="client"
								:instruction="instruction"/>
						</div>
					</v-tab>
					<v-tab id="leads" class="section--full" label="Leads">
						<header class="mb-2">
							<h2 class="beta heavy">Leads</h2>
						</header>
						<div v-if="!activeItem.leads.length" class="section section--empty">
							<div class="editorial center">
								<p class="beta">There are no leads related to this client.</p>
							</div>
							<ul class="actions actions--center actions--small-margin">
								<li v-if="client.instructions.length">
									<button
										class="button button--primary button--small"
										@click.prevent="goToTab('cases')">View cases
									</button>
								</li>
							</ul>
						</div>
						<div v-else class="sections">
							<Lead
								v-for="(lead, leadIndex) in client.leads"
								:key="leadIndex"
								:client="client" :goToTab="goToTab" :lead="lead"/>
						</div>
					</v-tab>
					<v-tab id="payments" class="section--full" label="Payments">
						<header class="mb-2">
							<h2 class="beta heavy">Payments</h2>
						</header>
						<div v-if="!activeItem.invoices.length" class="section section--empty">
							<div class="editorial center">
								<p class="beta">There are no payments related to this client.</p>
							</div>
						</div>
						<v-table v-else class="section--floating section--expanded">
							<thead>
								<tr>
									<th align="left">Invioce</th>
									<th align="center">Paid</th>
									<th align="right">Balance</th>
									<th align="right" class="actions"><span>Actions</span></th>
								</tr>
							</thead>
							<tfoot>
								<tr>
									<th align="left">Invioce</th>
									<th align="center">Paid</th>
									<th align="right">Balance</th>
									<th align="right" class="actions"><span>Actions</span></th>
								</tr>
							</tfoot>
							<tbody>
								<tr v-for="(invoice, invoiceIndex) in activeItem.invoices" :key="invoiceIndex">
									<td class="heavy" v-html="invoice.description"/>
									<td align="center">
										<bool-icon :value="invoice.paid"/>
									</td>
									<td align="right" v-html="$present('price', invoice.total)"/>
									<td v-html=""></td>
								</tr>
							</tbody>
						</v-table>
					</v-tab>
					<!--					<v-tab-->
					<!--						id="referrals"-->
					<!--						class="section&#45;&#45;full"-->
					<!--						label="Referrals">-->
					<!--						<header class="mb-2">-->
					<!--							<h2 class="beta heavy">Referrals</h2>-->
					<!--						</header>-->
					<!--						<div v-if="!activeItem.group.introductions.length" class="section section&#45;&#45;empty">-->
					<!--							<div class="editorial center">-->
					<!--								<p class="beta">There are no referrals related to this client.</p>-->
					<!--							</div>-->
					<!--						</div>-->
					<!--						<template-->
					<!--							v-for="(introduction, introductionIndex) in activeItem.group.introductions"-->
					<!--							v-else>-->
					<!--							<div class="sections">-->
					<!--								<article-->
					<!--									v-for="(service, serviceIndex) in introduction.services"-->
					<!--									:key="serviceIndex"-->
					<!--									class="section section&#45;&#45;flexed section&#45;&#45;floating service">-->
					<!--									<header class="flex gap-2 content-between items-center">-->
					<!--										<h3 v-html="service.names.singular"/>-->
					<!--										<status-badge-->
					<!--											v-if="!introduction.optIn"-->
					<!--											:status="introduction"-->
					<!--											group="introduction"/>-->
					<!--										<status-badge-->
					<!--											v-if="service.pivot.lead || service.pivot.instruction"-->
					<!--											:status="(service.pivot.lead || service.pivot.instruction).status"-->
					<!--											group="instruction"/>-->
					<!--									</header>-->
					<!--									<dl class="flex content-between gap-2 mt-2">-->
					<!--										<progress-indicator-->
					<!--											v-if="service.pivot.instruction?.completionPercentage"-->
					<!--											:percentage="service.pivot.instruction.completionPercentage.percent"-->
					<!--											class="metrics__metric&#45;&#45;progress&#45;&#45;small metrics__metric&#45;&#45;progress&#45;&#45;no-title"/>-->
					<!--										<div v-else-if="service.pivot.comparison?.quote">-->
					<!--											<dt class="heavy">Estimate</dt>-->
					<!--											<dd v-html="$present('price', service.pivot.comparison.quote.calculation.total)"/>-->
					<!--										</div>-->
					<!--									</dl>-->
					<!--									<ul class="actions actions&#45;&#45;right">-->
					<!--										<li>-->
					<!--											<router-link-->
					<!--												:to="{name: 'clients.referrals.show', params: {-->
					<!--									client: activeItem.id,-->
					<!--									introduction: introduction.id,-->
					<!--									service: service.key,-->
					<!--								}}"-->
					<!--												class="button button&#45;&#45;primary&#45;&#45;small">View</router-link>-->
					<!--										</li>-->
					<!--									</ul>-->
					<!--								</article>-->
					<!--								<button class="button button&#45;&#45;add">Add Referral</button>-->
					<!--							</div>-->
					<!--						</template>-->
					<!--					</v-tab>-->
				</v-tabs>
			</div>
		</div>
	</div>
</template>
<script>
import {mapActions, mapGetters} from "vuex";
import OutcomeBadge from "../../../../common/components/OutcomeBadge.vue";
import Address from "../../../../components/Address";
import opensFile from "../../../../mixins/opensFile";
import ClientDetails from "./ClientDetails.vue";
import Instruction from "./instruction/Instruction.vue";
import Lead from "./lead/Lead.vue";

export default {
	components: {
		Address,
		ClientDetails,
		Instruction,
		Lead,
		OutcomeBadge,
	},

	mixins: [opensFile],

	computed: {
		...mapGetters("clients", [
			"activeItem",
		]),
		client() {
			return this.activeItem;
		},
		tasksClass() {
			return {
				"icon--todo": this.numberOfCompleteTasks === 0,
				"icon--todo--some-checked": this.numberOfCompleteTasks && this.numberOfCompleteTasks !== this.numberOfTasks,
				"icon--todo--checked": this.numberOfCompleteTasks && this.numberOfCompleteTasks === this.numberOfTasks,
			};
		},
		numberOfTasks() {
			return Object.values(this.client.essentialTasks).length;
		},
		completeTasks() {
			return _.filter(this.client.essentialTasks, task => {
				return task.complete;
			});
		},
		numberOfCompleteTasks() {
			return Object.values(this.completeTasks).length;
		},
		leads() {
			return _.groupBy(this.activeItem.leads, lead => {
				return lead.service.key;
			});
		},
		hasLeads() {
			return Object.values(this.leads).length;
		},
		idCheck() {
			return this.client.idCheck;
		},
	},

	methods: {
		...mapActions("clients", [
			"activateModel",
			"refreshModel",
		]),
		checkClass(check) {
			return {
				"id-check__check-list__check--approve": check.recommendation === "approve",
				"id-check__check-list__check--consider": check.recommendation === "consider",
				"id-check__check-list__check--fail": check.recommendation === "fail",
			};
		},
		openPortalLinkModal(sendEmail = false) {
			this.$vmodal.open({
				component: () => import("./modals/PortalLink.vue"),
				props: {
					shouldSendEmail: sendEmail,
				},
			});
		},
		sendPortalEmail() {
			this.openPortalLinkModal(true);
		},
		openIdLinkModal(sendEmail = false) {
			this.$vmodal.open({
				component: () => import("./modals/IdCheckLink.vue"),
				props: {
					shouldSendEmail: sendEmail,
				},
			});
		},
		sendIdEmail() {
			this.openIdLinkModal(true);
		},
		openTaskListModal() {
			this.$vmodal.open({
				component: () => import("@/apps/portal/views/client/modals/TaskList.vue"),
			});
		},
		openRelatedClientsModal(instruction = null) {
			this.$vmodal.open({
				component: () => import("@/apps/portal/views/client/modals/RelatedClients.vue"),
				props: {
					instruction: instruction,
				},
			});
		},
		openIdCheckModal() {
			this.$vmodal.open({
				component: () => import("@/apps/portal/views/client/modals/IdCheck.vue"),
			});
		},
		openIssueIdCheckModal() {
			this.$vmodal.replace({
				component: () => import("./modals/IssueIdCheck.vue"),
			});
		},
	},

	watch: {
		"$route.params.client": {
			handler() {
				this.activateModel(this.$route.params.client);
			},
			immediate: true,
		},
	},
};
</script>
